import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'

import { useAppSelector } from '@/redux/hooks'
import { selectEditMode } from '@/redux/ui/uiSlice'

/**
 * **NOTE!!** Do _NOT_ use this on the Floor Plan! The moving of Devices is
 * the one place in the product that we can accidentally mutate things, and we
 * should still have that be an explicit action.
 *
 * Whether the global edit-mode requirement is satisfied for a gated action.
 *
 * With the `removeEditMode` feature flag on, we'll assume that Edit Mode as
 * it was previously known is "on" — but only for users who could previously
 * see Edit Mode (`shouldShowEditMode`). Otherwise the flag would expose
 * edit affordances to roles (e.g. Operators) whose call sites never paired
 * this hook with a permission check (ENG-32948). With this feature flag off
 * we'll fall back to Edit Mode of old.
 *
 * Pair it with the relevant `usePermissions` boolean at the call site:
 * ```ts
 * isDisabled={!shouldEnableCreateFacility || !isEditModeSatisfied}
 * ```
 *
 * This is the single place the flag/toggle combination lives — as edit mode is
 * retired, this hook collapses to `true` and its call sites delete cleanly.
 */
export const useIsEditModeSatisfied = (): boolean => {
  const isRemoveEditModeEnabled = useFeatureFlag(FeatureFlag.removeEditMode)
  const isDev = useFeatureFlag(FeatureFlag.development)
  const isEditModeOn = useAppSelector(selectEditMode)
  const canTurnOnEditMode = usePermissions().shouldShowEditMode

  // This is a double check that should only manifest in local envs and/or
  // internally with users constantly switching many accounts while still
  // having a single Role. e.x. while working on this I could validate things
  // working as expected, sign out, sign into an Operator, and see issues
  // This simple gate should ensure that we don't run into issues like that.
  const isEditMode = isEditModeOn && canTurnOnEditMode

  // `development` stands in for `removeEditMode` so dev envs exercise the
  // flag-on experience, but neither flag may bypass the permission gate
  // (ENG-32948) — roles that couldn't see Edit Mode gain nothing from them.
  const isEditModeAssumedOn = isRemoveEditModeEnabled || isDev

  return (isEditModeAssumedOn && canTurnOnEditMode) || isEditMode
}
